import React, { useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import queryString from 'query-string';
import ListingShareable from '../ListingShareable';
import RecentlySoldShareable from '../RecentlySoldShareable';
import RecentlySoldAreaStatsShareable from '../RecentlySoldAreaStatsShareable';
import LocalMarketShareable from '../LocalMarketShareable';
import MyListingShareable from '../MyListingShareable';
import DailyShareable from '../DailyShareable';
import Notable from '../Notable';
import ListpackShareable from '../ListpackShareable';
import TalkingPointsShareable from '../TalkingPointsShareable';
import CustomShareable from '../CustomShareable';
import MarketInsights from '../MarketInsights';
import { COLOR_WHITE, COLOR_STEAK_SAUCE } from '../colors';
import { getPalette } from './themes';
import { formatPhone } from '../../utils';

const ROOT_WIDTH = 1080;
const ROOT_HEIGHT = 1080;
const PIXEL_DENSITY_FACTOR = 2;
const CONTENT_BORDER_RADIUS = 14;
const COPYRIGHT_YEAR = new Date().getFullYear();

const useStyles = makeStyles(()=> ({
    root: {
        width: ROOT_WIDTH * PIXEL_DENSITY_FACTOR,
        height: ROOT_HEIGHT * PIXEL_DENSITY_FACTOR,
        position: 'relative',
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
        overflow: 'hidden',
        textRendering: 'optimizeLegibility',
    },
    viewport: {
        display: 'block',
        position: 'relative',
        width: '100%',
        height: '100%',
        zoom: PIXEL_DENSITY_FACTOR,
    },
    spotWrap: {
        margin: 0,
        minHeight: '100%',
        backgroundColor: COLOR_WHITE,
        position: 'relative',
        boxSizing: 'border-box',
        padding: '60px 90px 0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundImage: ({ primaryColor })=> `linear-gradient(
            to bottom,
            ${primaryColor},
            ${primaryColor} 72.5%,
            ${COLOR_WHITE} 72.5%
        )`,
    },
    taglineWrap: {
        textAlign: 'center',
        color: ({ secondaryColor })=> secondaryColor,
        padding: '0 22px',
        minHeight: 72, /* need this to preserve the layout in case there is no tagline */
        maxWidth: 900,
        margin: '0 auto',
    },
    quotes: {
        fontSize: '124px',
        lineHeight: '32px',
    },
    tagline: {
        fontSize: '32px',
        fontWeight: 700,
    },
    shareableWrap: {
        maxWidth: 900,
        margin: '40px auto',
    },
    shareableContainer: {
        width: '100%',
        height: 0,
        paddingBottom: '66.67%',
        borderRadius: CONTENT_BORDER_RADIUS,
        boxShadow: '0 0 16px 1px rgba(0,0,0,0.2)',
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        color: '#1a1a1a',
    },
    headshot: {
        width: 100,
        height: 100,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        borderRadius: '50%',
    },
    contact: {
        marginLeft: 24,
        flexGrow: 1,
    },
    name: {
        fontSize: '32px',
        fontWeight: 700,
    },
    title: {
        fontSize: '22px',
        fontWeight: 700,
    },
    brokerageLicenseNumber: {
        fontSize: '22px',
    },
    email: {
        marginTop: 0,
        fontSize: '22px',
    },
    phoneNumber: {
        fontSize: '22px',
        fontWeight: 700,
        marginLeft: 12,
    },
    website: {
        fontSize: '22px',
    },
    iconPhone: {
        '& path': {
            fill: ({ primaryColor })=> primaryColor,
        },
    },
    realtorLicenseNumber: {
        fontSize: '22px',
    },
    brandingWrapper: {
        marginLeft: 16,
        display: 'flex',
    },
    branding: {
        maxWidth: 186,
        minWidth: 186,
        height: 88,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
    },
    disclaimer: {
        marginTop: 14,
        color: COLOR_STEAK_SAUCE,
        fontSize: '12px',
        marginLeft: -74,
        marginRight: -74,
        textAlign: 'center',
    },
    premiumWatermark: {
        position: 'absolute',
        left: '50%',
        bottom: 96,
        zIndex: 9999,

        '& img': {
            position: 'relative',
            left: '-50%',
            width: 400,
            height: 400,
        },
    },
    trademarkContent: {
        display: 'flex',
        paddingTop: 10,
    },
    lrLogoWrap: {
        paddingRight: 30,
    },
    copyright: {
        fontSize: '16px',
        color: COLOR_STEAK_SAUCE,
        paddingTop: 5,
    },
}));


export default function BrandedShareable() {
    const location = useLocation();
    const [ contentImagesLoaded, setContentImagesLoaded ] = useState(false);
    let parsedData = null;
    let currentFrame = 1;
    let templateRootZoom = 1;
    try {
        const { data, frame, zoom } = queryString.parse(location.search);
        parsedData = JSON.parse(data);
        currentFrame = parseInt(frame, 10);
        if(zoom && !isNaN(parseFloat(zoom))) templateRootZoom = parseFloat(zoom);
    } catch(e) {
        console.error(e);
    }
    const {
        tagline,
        content,
        theme,
        user,
        watermark,
        type,
        palette,
    } = parsedData || {};
    const classes = useStyles(getPalette(theme, palette));
    const  {
        name,
        title,
        headshot,
        phone,
        office_phone,
        email,
        branding,
        website,
        brokerage_license_number,
        realtor_license_number,
        nmls_id,
        nmls_disclaimer,
        trademark,
    } = user || {};
    const formattedPhone = useMemo(()=> formatPhone(phone), [phone]);
    const formattedOfficePhone = useMemo(()=> formatPhone(office_phone), [office_phone]);
    const loadedBgs = useImagesLoaded([headshot, branding].filter((v)=> v && v.length));
    const onContentImagesLoaded = ()=> setContentImagesLoaded(true);

    useEffect(() => {
        if(loadedBgs && contentImagesLoaded && !window.isLoaded) {
            window.isLoaded = true;
        }
    }, [loadedBgs, contentImagesLoaded]);

    return (
        <>
            <link rel="stylesheet" href="./css/ll-circular-pro.css" />
            <div className={classes.root} id="templateRoot" style={{zoom: templateRootZoom}}>
                <div className={classes.viewport}>
                    <div className={classes.spotWrap}>

                        <div className={classes.taglineWrap}>
                            {tagline && (
                                <>
                                    {/*
                                        // Removing quotes to avoid user confusion
                                        // https://listreports.atlassian.net/browse/AG-1406
                                        <div className={classes.quotes}>&#8220;</div>
                                    */}
                                    <div className={classes.tagline}>{tagline}</div>
                                </>
                            )}
                        </div>

                        <div className={classes.shareableWrap}>
                            <div className={classes.shareableContainer}>
                                {(type === 'market_insights_shareable')
                                    ? <MarketInsights
                                        data={content}
                                        scaleFactor={1}
                                        borderRadius={CONTENT_BORDER_RADIUS}
                                        onImagesLoaded={onContentImagesLoaded}
                                    />
                                    : (type === 'local_housing_market_data')
                                        ? <LocalMarketShareable
                                            data={content}
                                            scaleFactor={1}
                                            borderRadius={CONTENT_BORDER_RADIUS}
                                            onImagesLoaded={onContentImagesLoaded}
                                        />
                                        : (type === 'my_listing_shareable')
                                            ? <MyListingShareable
                                                data={content}
                                                frame={currentFrame}
                                                scaleFactor={1}
                                                borderRadius={CONTENT_BORDER_RADIUS}
                                                onImagesLoaded={onContentImagesLoaded}
                                            />
                                                : (type === 'daily_shareable')
                                                    ? <DailyShareable
                                                        data={content || parsedData}
                                                        scaleFactor={1}
                                                        frame={currentFrame}
                                                        borderRadius={CONTENT_BORDER_RADIUS}
                                                        onImagesLoaded={onContentImagesLoaded}
                                                    />
                                                    : (type === 'notable')
                                                        ? <Notable
                                                            data={content || parsedData}
                                                            scaleFactor={1}
                                                            frame={currentFrame}
                                                            borderRadius={CONTENT_BORDER_RADIUS}
                                                            onImagesLoaded={onContentImagesLoaded}
                                                        />
                                                        : (type === 'listpack_shareable')
                                                            ? <ListpackShareable
                                                                data={content || parsedData}
                                                                scaleFactor={1}
                                                                frame={currentFrame}
                                                                borderRadius={CONTENT_BORDER_RADIUS}
                                                                onImagesLoaded={onContentImagesLoaded}
                                                            />
                                                            : (type === 'talking_points_shareable')
                                                                ? <TalkingPointsShareable
                                                                    data={content || parsedData}
                                                                    scaleFactor={1}
                                                                    frame={currentFrame}
                                                                    borderRadius={CONTENT_BORDER_RADIUS}
                                                                    onImagesLoaded={onContentImagesLoaded}
                                                                />
                                                                : (type === 'custom_shareable')
                                                                    ? <CustomShareable
                                                                        data={content || parsedData}
                                                                        scaleFactor={1}
                                                                        borderRadius={CONTENT_BORDER_RADIUS/2}
                                                                        onImagesLoaded={onContentImagesLoaded}
                                                                        border={`16px solid ${COLOR_WHITE}`}
                                                                    />
                                                                    : (content && content.area)
                                                                        ? <ListingShareable
                                                                            data={content}
                                                                            frame={currentFrame}
                                                                            scaleFactor={1}
                                                                            borderRadius={CONTENT_BORDER_RADIUS}
                                                                            onImagesLoaded={onContentImagesLoaded}
                                                                        />
                                                                        : (content && content.stats)
                                                                            ? <RecentlySoldAreaStatsShareable
                                                                                data={content}
                                                                                scaleFactor={1}
                                                                                borderRadius={CONTENT_BORDER_RADIUS}
                                                                                onImagesLoaded={onContentImagesLoaded}
                                                                            />
                                                                            : <RecentlySoldShareable
                                                                                data={content}
                                                                                frame={currentFrame}
                                                                                scaleFactor={1}
                                                                                borderRadius={CONTENT_BORDER_RADIUS}
                                                                                onImagesLoaded={onContentImagesLoaded}
                                                                            />
                                }
                            </div>
                        </div>

                        <div className={classes.userInfo}>
                            {headshot && (<div className={classes.headshot}
                                style={{backgroundImage: `url("${headshot}")`}}
                            ></div>)}

                            <div className={classes.contact}>
                                <div className={classes.name}>{name}</div>
                                <div className={classes.title}>{title}</div>
                                {formattedPhone && (
                                    <div>
                                        <svg className={classes.iconPhone}
                                            width="18"
                                            height="18"
                                            viewBox="0 0 18 18"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            >
                                            <path
                                                d="M3.62 7.79C5.06 10.62 7.38 12.93 10.21 14.38L12.41 12.18C12.68 11.91 13.08 11.82 13.43 11.94C14.55 12.31 15.76 12.51 17 12.51C17.55 12.51 18 12.96 18 13.51V17C18 17.55 17.55 18 17 18C7.61 18 0 10.39 0 1C0 0.45 0.45 0 1 0H4.5C5.05 0 5.5 0.45 5.5 1C5.5 2.25 5.7 3.45 6.07 4.57C6.18 4.92 6.1 5.31 5.82 5.59L3.62 7.79Z"
                                            />
                                        </svg>
                                        <span className={classes.phoneNumber}>{formattedPhone}</span>
                                    </div>
                                )}

                                <div className={classes.email}>{email}</div>

                                {website && (<div className={classes.website}>{website}</div>)}

                                {realtor_license_number && (<div className={classes.realtorLicenseNumber}>License # {realtor_license_number}</div>)}
                                {nmls_id && (<div className={classes.realtorLicenseNumber}>NMLS ID # {nmls_id}</div>)}

                                {trademark && (
                                    <div className={classes.trademarkContent}>
                                        <div className={classes.lrLogoWrap}>
                                            <div>
                                                <img src="./images/lr-logo-disclaimer-black.svg" alt="LR Logo" />
                                            </div>
                                            <div className={classes.copyright}>
                                                © ListReports {COPYRIGHT_YEAR}, all rights reserved
                                            </div>
                                        </div>
                                        <div className={classes.equalHousingWrap}>
                                            <img src="./images/equal-housing-logo-black.svg" alt="Equal Housing Logo" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={classes.brandingWrapper} style={{ alignItems: (office_phone || brokerage_license_number) ? 'start' : 'center' }}>
                                <div>
                                    {branding && (<div className={classes.branding}
                                        style={{backgroundImage: `url("${branding}")`}}
                                    ></div>)}
                                    {formattedOfficePhone && (
                                        <div>
                                            <svg className={classes.iconPhone}
                                                width="18"
                                                height="18"
                                                viewBox="0 0 18 18"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                >
                                                <path
                                                    d="M3.62 7.79C5.06 10.62 7.38 12.93 10.21 14.38L12.41 12.18C12.68 11.91 13.08 11.82 13.43 11.94C14.55 12.31 15.76 12.51 17 12.51C17.55 12.51 18 12.96 18 13.51V17C18 17.55 17.55 18 17 18C7.61 18 0 10.39 0 1C0 0.45 0.45 0 1 0H4.5C5.05 0 5.5 0.45 5.5 1C5.5 2.25 5.7 3.45 6.07 4.57C6.18 4.92 6.1 5.31 5.82 5.59L3.62 7.79Z"
                                                />
                                            </svg>
                                            <span className={classes.phoneNumber}>{formattedOfficePhone}</span>
                                        </div>
                                    )}
                                    {brokerage_license_number && (<div className={classes.brokerageLicenseNumber}>License # {brokerage_license_number}</div>)}
                                </div>
                            </div>
                        </div>


                        {nmls_disclaimer && (<div className={classes.disclaimer}>{nmls_disclaimer}</div>)}


                        {watermark && (<div className={classes.premiumWatermark}>
                            <img src="./images/watermark-stamp.svg" alt="Watermark" />
                        </div>)}
                    </div>
                </div>
            </div>
        </>
    );
}
